import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Church, BedDouble, Car, HeartHandshake, MapPin, Phone, MessageCircle, ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { CONTACT, IMG, ROOM_IMAGES } from "@/data/site";
import { Reveal, MaskedLine, Eyebrow } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import Seo from "@/components/Seo";

const BENEFIT_ICONS = [Church, BedDouble, Car, HeartHandshake];

function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden bg-[#1F2421]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Sobe Bevanda — family guesthouse in Međugorje"
          className="w-full h-full object-cover"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F2421]/85 via-[#1F2421]/35 to-[#1F2421]/25" />
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-20 sm:pb-28 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="inline-flex items-center gap-2 rounded-full border border-[#F9F8F3]/25 bg-[#1F2421]/30 backdrop-blur-sm px-4 py-2 mb-8"
        >
          <MapPin size={13} className="text-[#D8CFBC]" />
          <span data-testid="hero-distance-tag" className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#F9F8F3]/90">
            {t.hero.tag}
          </span>
        </motion.div>

        <h1 className="font-serif-display font-light text-[#F9F8F3] leading-[0.95] text-5xl sm:text-6xl lg:text-8xl tracking-tight">
          <MaskedLine delay={0.35}>{t.hero.headline[0]}</MaskedLine>
          <MaskedLine delay={0.5} className="italic text-[#D8CFBC]">{t.hero.headline[1]}</MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="font-serif-display text-2xl sm:text-3xl text-[#F9F8F3]/90 mt-6"
        >
          {t.hero.sub}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="text-[#F9F8F3]/70 text-base sm:text-lg font-light mt-3 max-w-xl"
        >
          {t.hero.support}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="flex flex-wrap items-center gap-4 mt-10"
        >
          <Link
            to="/kontakt"
            data-testid="hero-book-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-[#F9F8F3] text-[#1F2421] font-semibold text-sm px-7 py-3.5 hover:bg-[#D8CFBC] transition-colors duration-300"
          >
            {t.cta.book}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/sobe"
            data-testid="hero-explore-rooms-cta"
            className="inline-flex items-center gap-2 rounded-full border border-[#F9F8F3]/40 text-[#F9F8F3] text-sm font-medium px-7 py-3.5 hover:bg-[#F9F8F3]/10 transition-colors duration-300"
          >
            {t.cta.exploreRooms}
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Benefits() {
  const { t } = useLang();
  return (
    <section data-testid="benefits-section" className="border-b border-[#1F2421]/8 bg-[#F9F8F3]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12 sm:py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {t.benefits.map((b, i) => {
          const Icon = BENEFIT_ICONS[i];
          return (
            <Reveal key={i} delay={i * 0.08} className="flex flex-col gap-3">
              <Icon size={22} strokeWidth={1.5} className="text-[#4A5D3E]" />
              <div>
                <p className="font-medium text-[#1F2421] text-sm sm:text-base leading-snug">{b.title}</p>
                <p className="text-[#7A8278] text-xs sm:text-sm mt-1">{b.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function About() {
  const { t } = useLang();
  return (
    <section data-testid="about-section" className="py-20 sm:py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="md:col-span-5 flex items-center justify-center">
          <Reveal className="w-full">
            <div className="bg-white rounded-2xl border border-[#1F2421]/8 shadow-[0_20px_50px_-25px_rgba(31,36,33,0.2)] px-10 py-14 sm:px-16 sm:py-20 flex items-center justify-center">
              <img
                src="/images/logo-bevanda.jpg"
                alt="Sobe Bevanda Međugorje — logotip"
                className="w-full max-w-xs sm:max-w-sm h-auto"
              />
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-7 lg:pl-8 mt-10 md:mt-0">
          <Reveal>
            <Eyebrow>{t.about.eyebrow}</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2421] leading-tight max-w-xl">
              {t.about.title}
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-lg sm:text-xl text-[#4A504B] leading-relaxed font-light mt-7 max-w-2xl">{t.about.p1}</p>
            <p className="text-base text-[#4A504B] leading-relaxed mt-5 max-w-2xl">{t.about.p2}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  const { t } = useLang();
  return (
    <section data-testid="manifesto-section" className="bg-[#F3EFE6] py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <Eyebrow>{t.manifesto.eyebrow}</Eyebrow>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
          {t.manifesto.chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12} className="border-t border-[#1F2421]/15 pt-6">
              <span className="font-serif-display italic text-5xl text-[#4A5D3E]/30">{c.n}</span>
              <h3 className="font-serif-display text-2xl text-[#1F2421] mt-4">{c.title}</h3>
              <p className="text-sm sm:text-base text-[#4A504B] leading-relaxed mt-3">{c.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoomsPreview() {
  const { t } = useLang();
  return (
    <section data-testid="rooms-preview-section" className="py-20 sm:py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <Reveal>
            <Eyebrow>{t.roomsSection.eyebrow}</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2421]">{t.roomsSection.title}</h2>
            <p className="text-[#4A504B] mt-4 max-w-xl">{t.roomsSection.text}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/sobe" data-testid="rooms-preview-all-cta" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#4A5D3E] hover:text-[#3A4A31] transition-colors">
              {t.roomsSection.cta}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {t.roomsPage.items.map((room, i) => (
            <Reveal key={room.id} delay={i * 0.08}>
              <Link
                to="/sobe"
                data-testid={`room-card-${room.id}`}
                className="group block bg-white rounded-xl overflow-hidden border border-[#1F2421]/8 hover:shadow-[0_20px_50px_-20px_rgba(31,36,33,0.25)] transition-shadow duration-500"
              >
                <div className="overflow-hidden aspect-[4/3]">
                  <img
                    src={ROOM_IMAGES[room.id]}
                    alt={room.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                </div>
                <div className="p-5">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A8278]">
                    {room.capacity} {Number(room.capacity) === 1 ? t.roomsPage.guest : t.roomsPage.guests}
                  </p>
                  <h3 className="font-serif-display text-xl text-[#1F2421] mt-1.5">{room.name}</h3>
                  <p className="text-sm text-[#4A504B] mt-2 line-clamp-2">{room.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#4A5D3E] mt-4">
                    {t.cta.viewRoom} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocationSection() {
  const { t } = useLang();
  return (
    <section data-testid="location-section" className="bg-[#F3EFE6] py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <Eyebrow>{t.location.eyebrow}</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2421]">{t.location.title}</h2>
          <p className="text-base sm:text-lg text-[#4A504B] leading-relaxed mt-6 max-w-lg">{t.location.text}</p>
          <ul className="mt-8 space-y-4">
            {[t.location.point1, t.location.point2, t.location.point3].map((p, i) => (
              <li key={i} className="flex items-center gap-3 text-sm sm:text-base text-[#1F2421]">
                <span className="h-2 w-2 rounded-full bg-[#4A5D3E] shrink-0" />
                {p}
              </li>
            ))}
          </ul>
          <a
            href={CONTACT.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="get-directions-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-[#4A5D3E] hover:bg-[#3A4A31] text-[#F9F8F3] text-sm font-semibold px-6 py-3 mt-10 transition-colors duration-300"
          >
            {t.cta.directions}
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="overflow-hidden rounded-2xl border-8 border-white shadow-[0_30px_60px_-30px_rgba(31,36,33,0.35)]">
            <iframe
              title="Sobe Bevanda — Međugorje map"
              src={CONTACT.mapEmbed}
              className="w-full h-[320px] sm:h-[420px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function AmenitiesSection() {
  const { t } = useLang();
  return (
    <section data-testid="amenities-section" className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <Eyebrow>Sobe Bevanda</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2421]">{t.homeAmenities.title}</h2>
          <p className="text-[#4A504B] mt-4 max-w-xl">{t.homeAmenities.text}</p>
        </Reveal>
        <div className="flex flex-wrap gap-3 mt-10">
          {t.roomsPage.shared.map((a, i) => (
            <Reveal key={a} delay={i * 0.05}>
              <span className="inline-flex items-center rounded-full border border-[#4A5D3E]/25 bg-white px-5 py-2.5 text-sm text-[#1F2421]">
                {a}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function DiningSection() {
  const { t } = useLang();
  return (
    <section data-testid="dining-section" className="bg-[#F3EFE6] py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Eyebrow>{t.diningPreview.eyebrow}</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2421]">{t.diningPreview.title}</h2>
          <p className="text-base sm:text-lg text-[#4A504B] leading-relaxed mt-6 max-w-lg">{t.diningPreview.text}</p>
          <Link to="/dining" data-testid="dining-section-cta" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#4A5D3E] hover:text-[#3A4A31] mt-8 transition-colors">
            {t.diningPreview.cta}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="overflow-hidden rounded-2xl">
            <img src={IMG.breakfast} alt="Blagovaonica Sobe Bevanda" loading="lazy" className="w-full aspect-[16/10] object-cover hover:scale-[1.03] transition-transform duration-700" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function MedjSection() {
  const { t } = useLang();
  return (
    <section data-testid="medjugorje-section" className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <Reveal className="order-2 lg:order-1">
          <div className="overflow-hidden rounded-2xl">
            <img src="/images/podbrdo.jpg" alt="Brdo ukazanja Podbrdo u Međugorju" loading="lazy" className="w-full aspect-[16/10] object-cover hover:scale-[1.03] transition-transform duration-700" />
          </div>
        </Reveal>
        <Reveal delay={0.12} className="order-1 lg:order-2">
          <Eyebrow>Međugorje</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2421]">{t.homeMedj.title}</h2>
          <p className="text-base sm:text-lg text-[#4A504B] leading-relaxed mt-6 max-w-lg">{t.homeMedj.text}</p>
          <Link to="/medjugorje" data-testid="medjugorje-section-cta" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#4A5D3E] hover:text-[#3A4A31] mt-8 transition-colors">
            {t.homeMedj.cta}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useLang();
  const waUrl = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(t.contactPage.waMessage)}`;
  return (
    <section data-testid="final-cta-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img src="/images/krizevac.jpg" alt="View over Međugorje from Cross Mountain" loading="lazy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1F2421]/70" />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 text-center">
        <Reveal>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F9F8F3] leading-tight">{t.finalCta.title}</h2>
          <p className="text-[#F9F8F3]/75 text-base sm:text-lg font-light mt-6 max-w-2xl mx-auto">{t.finalCta.text}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <Link
              to="/kontakt"
              data-testid="final-cta-inquiry"
              className="inline-flex items-center rounded-full bg-[#F9F8F3] text-[#1F2421] text-sm font-semibold px-7 py-3.5 hover:bg-[#D8CFBC] transition-colors duration-300"
            >
              {t.cta.sendInquiry}
            </Link>
            <a
              href={CONTACT.phoneHref}
              data-testid="final-cta-call"
              className="inline-flex items-center gap-2 rounded-full border border-[#F9F8F3]/40 text-[#F9F8F3] text-sm font-medium px-7 py-3.5 hover:bg-[#F9F8F3]/10 transition-colors duration-300"
            >
              <Phone size={15} /> {t.cta.call}
            </a>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full border border-[#F9F8F3]/40 text-[#F9F8F3] text-sm font-medium px-7 py-3.5 hover:bg-[#F9F8F3]/10 transition-colors duration-300"
            >
              <MessageCircle size={15} /> WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, lang } = useLang();
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "LodgingBusiness",
      name: "Sobe Bevanda",
      description: t.seo.home.desc,
      url: window.location.origin,
      telephone: "+38763177732",
      email: "sobe.bevanda@outlook.com",
      address: { "@type": "PostalAddress", streetAddress: "Ivana Pavla II 7C", addressLocality: "Međugorje", postalCode: "88266", addressCountry: "BA" },
      geo: { "@type": "GeoCoordinates", latitude: 43.1918399, longitude: 17.6769151 },
      image: [
        window.location.origin + "/images/hero.jpg",
        window.location.origin + "/images/room-double.jpg",
        window.location.origin + "/images/blagovaonica.jpg",
      ],
      amenityFeature: [
        { "@type": "LocationFeatureSpecification", name: "Free WiFi", value: true },
        { "@type": "LocationFeatureSpecification", name: "Free private parking", value: true },
      ],
    },
    { "@context": "https://schema.org", "@type": "WebSite", name: "Sobe Bevanda", url: window.location.origin },
  ];
  return (
    <div data-testid="home-page" key={lang}>
      <Seo title={t.seo.home.title} description={t.seo.home.desc} image="/images/hero.jpg" jsonLd={jsonLd} />
      <Hero />
      <Benefits />
      <About />
      <Manifesto />
      <RoomsPreview />
      <Marquee />
      <LocationSection />
      <AmenitiesSection />
      <DiningSection />
      <MedjSection />
      <FinalCTA />
    </div>
  );
}
